import { makeStyles, Theme } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { useLayoutEffect, useRef, useState } from "react";
import { spaceBetween } from "../../../../../../common/util/Array/spaceBetween";
import ShurikenProgress from "../../../../Animations/ShurikenProgress";
import { DarkPopoverMenu } from "../../../../Popover/DarkPopoverMenu";
import { openOtherUserPanel } from "../../../../User/OtherUserPanel/functions";
import { User } from "../../../../User/types";
import { UserAvatar } from "../../../../User/UserAvatar/UserAvatar";
import { Message } from "../../types";
import { MyOwnPresent } from "./components/MyOwnPresent";
import { SentTime } from "./components/SentTime";
import { ImageMessage } from "./ImageMessage";
import { MessageMarkdown } from "./MessageMarkdown";

export function MyMessage({
    me,
    theirUserId,
    currentlySentMessageNo,
    message: {
        text,
        messageNo,
        minutesAgo,
        present,
        presentQuantity,
        unremovable,
        isSending,
        isImage,
    },
    unsendMessage,
}: {
    me: User;
    theirUserId: number;
    currentlySentMessageNo: number;
    message: Message;
    unsendMessage: () => void;
}) {
    const deleteIconRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [unsendOpen, setUnsendOpen] = useState(false);

    const c = useStyles({ isImage });

    useLayoutEffect(() => {
        if (currentlySentMessageNo === messageNo) {
            containerRef.current?.scrollIntoView();
        }
    }, [currentlySentMessageNo, messageNo]);

    return (
        <div className={c.container} ref={containerRef}>
            <UserAvatar
                user={me}
                colorNumber={me.level}
                size={40}
                onClick={() => {
                    openOtherUserPanel(me.userId);
                }}
                className="pointer"
            />
            <div className={spaceBetween(c.comment, c.colorChange)}>
                <div className={spaceBetween(c.commentTail, c.colorChange)} />

                {isImage ? (
                    <ImageMessage
                        myUserId={me.userId}
                        targetUserId={theirUserId}
                        messageNo={messageNo}
                        messageType={"MyMessage"}
                        isSending={isSending}
                    />
                ) : (
                    <div className={c.markdownContainer}>
                        <MessageMarkdown text={text} />
                    </div>
                )}

                {present && (
                    <MyOwnPresent
                        present={present}
                        presentQuantity={presentQuantity}
                    />
                )}
            </div>
            <div className={c.timeAndIconOuterContainer}>
                {isSending ? (
                    <ShurikenProgress
                        size={20}
                        style={{ paddingBottom: 10, paddingRight: 5 }}
                    />
                ) : (
                    <div className={c.timeAndIconInnerContainer}>
                        {!unremovable && (
                            <DeleteIcon
                                className={c.deleteIcon}
                                ref={deleteIconRef}
                                onClick={() => {
                                    setUnsendOpen(true);
                                }}
                            />
                        )}
                        <SentTime minutesAgo={minutesAgo} />
                    </div>
                )}
            </div>
            <DarkPopoverMenu
                open={unsendOpen}
                anchorEl={deleteIconRef.current}
                containerEl={containerRef.current}
                onClose={() => {
                    setUnsendOpen(false);
                }}
                items={[
                    {
                        label: "Unsend",
                        icon: <DeleteIcon />,
                        onClick: unsendMessage,
                    },
                ]}
            />
        </div>
    );
}
const useStyles = makeStyles<Theme, { isImage: boolean }>(theme => ({
    container: {
        display: "flex",
        flexDirection: "row-reverse",
        width: "100%",
        marginBottom: 10,
    },
    comment: ({ isImage }) => ({
        minHeight: 40,
        color: "white",
        marginRight: 20,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: isImage ? 3 : "13px 15px 15px",
        borderRadius: 10,
        position: "relative",
        backgroundColor: theme.palette.success.main,
    }),
    commentTail: {
        position: "absolute",
        width: 20,
        height: 20,
        top: 10,
        right: -5,
        transform: "rotate(45deg)",
        backgroundColor: theme.palette.success.main,
    },
    colorChange: {
        animation: "$colorChange 300ms",
    },
    "@keyframes colorChange": {
        "0%": {
            backgroundColor: theme.palette.grey[200],
        },
        "100%": {
            backgroundColor: theme.palette.success.main,
        },
    },
    timeAndIconOuterContainer: {
        width: 60,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        padding: 5,
        fontSize: "small",
    },
    timeAndIconInnerContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    deleteIcon: {
        color: theme.palette.grey[800],
        cursor: "pointer",
    },
    markdownContainer: {
        zIndex: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
}));
